package com.qrkontaq.ui.tenant

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.qrkontaq.core.config.AppConfig
import com.qrkontaq.ui.theme.Brand
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.w3c.dom.MessageEvent
import org.w3c.dom.events.Event

private external fun encodeURIComponent(str: String): String

/**
 * Opens the central auth popup (auth.qrkontaq.com) — the ONLY origin where the
 * OAuth flows may run, since Google/Apple don't allow wildcard venue subdomains.
 * In-app browsers that block popups fall back to a full redirect; the session
 * then arrives via the SSO cookie on return.
 */
internal fun openGuestAuthPopup(provider: String, lang: String) {
    val url =
        "${AppConfig.authOrigin}/auth?provider=$provider" +
            // The auth page itself is only localized in en/uk; other menu
            // languages fall back to English there.
            "&lang=" + (if (lang == "uk") "uk" else "en") +
            "&return_origin=" + encodeURIComponent(window.location.origin)
    val popup = window.open(url, "qk-auth", "popup=yes,width=470,height=640")
    if (popup == null) {
        window.location.href =
            url + "&mode=redirect&return_url=" + encodeURIComponent(window.location.href)
    }
}

/**
 * Subscribes to postMessage results from the auth popup; fills GuestSession and
 * calls [onSuccess] on a valid token. Returns a disposer for onDispose.
 */
internal fun listenForGuestAuth(onSuccess: () -> Unit): () -> Unit {
    val listener: (Event) -> Unit = listener@{ event ->
        val msg = event.unsafeCast<MessageEvent>()
        if (msg.origin != AppConfig.authOrigin) return@listener
        val data = msg.data.asDynamic()
        if (data == null || data.type != "qk-auth-success") return@listener
        val token = data.accessToken as? String
        if (!token.isNullOrBlank()) {
            GuestSession.token = token
            (data.email as? String)?.let { GuestSession.email = it }
            GuestSession.oauthProvider = (data.provider as? String) ?: "google"
            onSuccess()
        }
    }
    window.addEventListener("message", listener)
    return { window.removeEventListener("message", listener) }
}

/**
 * Guest sign-in panel: Google and Apple. The flows run on the fixed auth origin
 * in a POPUP; the token comes back via postMessage, so the guest never visually
 * leaves the menu.
 */
@Composable
internal fun GuestAuthPanel(
    lang: String,
    onSuccess: () -> Unit,
) {
    var notice by remember { mutableStateOf<String?>(null) }

    DisposableEffect(Unit) {
        val dispose = listenForGuestAuth {
            notice = null
            onSuccess()
        }
        onDispose { dispose() }
    }

    fun openAuth(provider: String) = openGuestAuthPopup(provider, lang)

    Column(
        Modifier
            .fillMaxWidth()
            .backgroundColor(Brand.WarmBackground)
            .borderRadius(14.px)
            .padding(14.px),
    ) {
        SpanText(
            menuT(lang).signInHint,
            Modifier
                .fontSize(12.px)
                .color(Brand.TextSecondary)
                .margin(bottom = 12.px)
                .styleModifier { property("line-height", "1.5") },
        )

        // Google
        Row(
            Modifier
                .fillMaxWidth()
                .backgroundColor(Brand.Surface)
                .border(1.px, LineStyle.Solid, Brand.Divider)
                .borderRadius(11.px)
                .padding(topBottom = 10.px)
                .margin(bottom = 8.px)
                .cursor(Cursor.Pointer)
                .attrsModifier { classes("qk-press"); attr("role", "button") }
                .onClick { openAuth("google") },
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            InlineIcon(MenuIcons.GoogleLogo, 16, "#000000")
            SpanText(
                menuT(lang).continueWithGoogle,
                Modifier
                    .fontSize(13.px)
                    .fontWeight(FontWeight.SemiBold)
                    .color(Brand.Charcoal)
                    .margin(left = 8.px),
            )
        }

        // Apple
        Row(
            Modifier
                .fillMaxWidth()
                .backgroundColor(Brand.Charcoal)
                .borderRadius(11.px)
                .padding(topBottom = 10.px)
                .cursor(Cursor.Pointer)
                .attrsModifier { classes("qk-press"); attr("role", "button") }
                .onClick { openAuth("apple") },
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            InlineIcon(MenuIcons.AppleLogo, 16, "#FFFFFF")
            SpanText(
                menuT(lang).continueWithApple,
                Modifier
                    .fontSize(13.px)
                    .fontWeight(FontWeight.SemiBold)
                    .color(Brand.Surface)
                    .margin(left = 8.px),
            )
        }

        notice?.let {
            SpanText(it, Modifier.fontSize(12.px).color(Brand.TextSecondary).margin(top = 8.px))
        }
    }
}
